import { Flex, Text } from '@chakra-ui/react'
import { Plus, X } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BlockedNumber, useBlockedNumbers } from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { Size } from '~/types/ThemeTypes'
import { focusAfterDialogsClose } from '~/utils/focusAfterDialogsClose'
import { formatPhoneNumber } from '~/utils/formatter'

import { useBlockedNumberStyles } from './componentStyles/BlockedNumbers.styles'
import { DefaultSettingsWrapper } from './Shared'
import { BlockNumberModal } from './Shared/BlockedNumberModal'

const UNBLOCK_BUTTON_SELECTOR = '[data-unblock-button]'

export const BlockedNumbers = () => {
  const { t } = useTranslation()
  const { blockedNumbers, unblockNumber, blockNumber, isBlocking } =
    useBlockedNumbers({ forceRefresh: true })
  const {
    confirmModal: [, setConfirmModalState],
  } = useModalState()

  const styles = useBlockedNumberStyles()

  const [isBlockModalOpen, setBlockModalOpen] = useState(false)
  const announce = useAnnounce()
  const addButtonRef = useRef<HTMLButtonElement>(null)
  const numberListRef = useRef<HTMLDivElement>(null)
  const focusIndexAfterUnblock = useRef<number | null>(null)

  useEffect(() => {
    const pendingIndex = focusIndexAfterUnblock.current
    if (pendingIndex === null) return
    return focusAfterDialogsClose(() => {
      focusIndexAfterUnblock.current = null
      const unblockButtons =
        numberListRef.current?.querySelectorAll<HTMLButtonElement>(
          UNBLOCK_BUTTON_SELECTOR,
        ) ?? []
      const nextButton =
        unblockButtons[Math.min(pendingIndex, unblockButtons.length - 1)]
      return nextButton ?? addButtonRef.current
    })
  }, [blockedNumbers])

  const handleBlockModalOpen = () => {
    setBlockModalOpen(true)
  }

  const handleBlockModalClose = () => {
    setBlockModalOpen(false)
  }

  const handleUnblockClick = (blockedNumber: BlockedNumber, index: number) => {
    const formattedPhoneNumber = formatPhoneNumber(blockedNumber.phoneNumber)
    setConfirmModalState({
      title: t('blockedNumbers.confirmUnblock.title', { formattedPhoneNumber }),
      message: t('blockedNumbers.confirmUnblock.message'),
      confirmText: t('blockedNumbers.confirmUnblock.confirmText'),
      cancelText: t('blockedNumbers.confirmUnblock.cancelText'),
      onConfirm: () => {
        unblockNumber(blockedNumber.id, blockedNumber.phoneNumber, {
          onUnblocked: () => {
            focusIndexAfterUnblock.current = index
            announce(
              t('blockedNumbers.numberUnblocked', { formattedPhoneNumber }),
            )
          },
        })
      },
    })
  }

  const blockedNumberContent =
    blockedNumbers && blockedNumbers.length > 0 ? (
      blockedNumbers.map((blockedNumber, index) => {
        const formattedPhoneNumber = formatPhoneNumber(
          blockedNumber.phoneNumber,
        )
        return (
          <Flex key={blockedNumber.id} css={styles.blockedNumberWrapper}>
            <Text css={styles.blockedNumberText} color="fg.default">
              {formattedPhoneNumber}
            </Text>
            <QuickActionButton
              data-unblock-button
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              iconProps={{ boxSize: '0.625rem' }}
              actionIcon={X}
              tooltipText={t('blockedNumbers.unblockTooltip')}
              ariaLabel={t('blockedNumbers.unblockNumberLabel', {
                formattedPhoneNumber,
              })}
              onClick={() => handleUnblockClick(blockedNumber, index)}
            />
          </Flex>
        )
      })
    ) : (
      <Text>{t('blockedNumbers.noBlockedNumbers')}</Text>
    )

  return (
    <DefaultSettingsWrapper>
      <Flex css={styles.outerWrapper}>
        <BlockNumberModal
          isOpen={isBlockModalOpen}
          onClose={handleBlockModalClose}
          onBlockNumber={blockNumber}
          isLoading={isBlocking}
        />
        <Flex css={styles.wrapper}>
          <Flex css={styles.headerWrapper}>
            <Flex css={styles.header}>
              <Text css={styles.headerText} color="fg.default">
                {t('blockedNumbers.header')}
              </Text>
              <MangoButton
                ref={addButtonRef}
                variant={MangoButtonVariant.Default_200}
                size={Size.sm}
                leftIcon={Plus}
                onClick={handleBlockModalOpen}
                leftIconProps={{
                  boxSize: '10px',
                  position: 'relative',
                  bottom: '.5px',
                  stroke: 'inherit',
                  strokeWidth: '2px',
                }}
              >
                {t('blockedNumbers.addNumber')}
              </MangoButton>
            </Flex>
            <Text css={styles.headerSubtext} color="fg.default">
              {t('blockedNumbers.headerSubtext')}
            </Text>
          </Flex>
          <Flex ref={numberListRef} css={styles.numberWrapper}>
            {blockedNumberContent}
          </Flex>
        </Flex>
      </Flex>
    </DefaultSettingsWrapper>
  )
}
